import { Eye, EyeOff, Loader2 } from "lucide-react"
import { useEffect, useState, type ComponentProps } from "react"

import { Button } from "@/components/ui/button"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ScenarioNotice } from "@/components/ScenarioNotice"
import { useEuSign } from "@/hooks/useEuSign"
import { useEuSignFile } from "@/hooks/useEuSignFile"
import type {
  CertificateAuthority,
  EUSignLibraryAdapter,
  KeyMediaDevice,
} from "iit/browser"

function keyId(key: KeyMediaDevice) {
  return `${key.typeIndex}-${key.devIndex}`
}

function readFileBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (reader.readyState !== FileReader.DONE) return
      resolve(new Uint8Array(reader.result as ArrayBuffer))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
  })
}

// Radix `Select` reserves "" for "no value", so "let the library find the
// provider itself" (no `caCN` — it polls every CA in CAs.json) needs its own
// sentinel value.
const AUTO_CA = "auto"
const CA_STORAGE_KEY = "kep.issuerCN"

function readStoredCa(): string {
  try {
    return localStorage.getItem(CA_STORAGE_KEY) || AUTO_CA
  } catch {
    return AUTO_CA
  }
}

function storeCa(value: string) {
  try {
    if (value === AUTO_CA) localStorage.removeItem(CA_STORAGE_KEY)
    else localStorage.setItem(CA_STORAGE_KEY, value)
  } catch {
    // Remembering the provider is a convenience only.
  }
}

// Dev-only escape hatch (Test/JS/main.js's PK_FORM_TYPE_FILE path): lets a
// developer read a КЕП from an uploaded key file instead of a hardware
// medium, so the sign flow can be exercised without owning a token or
// installing the native ІІТ sign agent. Never shown in production.

//const fileModeAvailable = import.meta.env.DEV
const fileModeAvailable = true

/**
 * The "read a key" step of the КЕП signing flow
 * (docs/subekt-otk/03-wireframes/design-system.md's `KepKeyReader` contract:
 * a connected-key `Select` + a password `Input`, two fields only) — backed
 * here by the real `EUSignLibraryAdapter` (`enumKeys`/`readPrivateKey`) via
 * the `useEuSign` hook, instead of the wireframe's mocked `connectedKepKeys`
 * list. The library loads lazily on this component's own mount rather than
 * blocking the whole app at startup — most sessions on this page never touch
 * KEP at all (e-mail/OTP login never needs it).
 *
 * Gap vs. the documented contract: the design doc's key `Select` shows a
 * `${provider} · сертифікат до ${validUntil}` description per selected key.
 * `EUSignLibraryAdapter.enumKeys()` only returns device identity
 * (`typeName`/`devName`) — provider and certificate validity aren't known
 * until *after* a successful `readPrivateKey`, and the adapter exposes no
 * separate "inspect the certificate on this device" call. This component
 * shows the device's own type/name in that slot instead of inventing
 * provider/validUntil values.
 *
 * Deviation (09.10.2026): adds a provider `Select` («Надавач електронних
 * довірчих послуг») despite the documented "two fields, no provider Select"
 * decision — without `caCN` the library polls every CA in CAs.json until one
 * returns the key's certificate. «Визначити автоматично» keeps that old
 * behaviour; the last choice is remembered per browser.
 *
 * The wrong-password/read-failure notice below is also not part of the
 * documented contract (the wireframe's version is decorative and can't
 * fail) — it's the minimum needed for this real version to be usable.
 */
export function KepKeyReader({
  actionLabel = "Зчитати",
  actionSize,
  disabled,
  busy = false,
  onRead,
}: {
  /** Overrides the button's label for callers using this as their actual sign/confirm action rather than an intermediate read-then-confirm step. */
  actionLabel?: string
  /** No default — omitting it renders `Button`'s own default size, unchanged for every existing consumer. Only `SignDialogForm` passes `"lg"`. */
  actionSize?: ComponentProps<typeof Button>["size"]
  disabled?: boolean
  /** Caller is still processing the key `onRead` handed it (e.g. signing/verifying a challenge) — locks the form and swaps the action button to a spinner, distinct from `disabled` which callers also use for plain "not ready yet" gating unrelated to being busy. */
  busy?: boolean
  /**
   * Called once a key was just read, with the adapter callers sign with and
   * a display label for whichever carrier produced it — so a caller can
   * echo the chosen carrier back (e.g. in a confirmation dialog) without
   * re-deriving it itself. Widened this pass from a bare `adapter` argument
   * (`design-system.md`'s own documented widening is a decorative
   * `{ keyLabel }`-only shape for the wireframe's mocked keys; this real
   * version keeps `adapter` too since callers still need it to actually
   * sign).
   */
  onRead: (info: { adapter: EUSignLibraryAdapter; keyLabel: string }) => void
}) {
  const [mode, setMode] = useState<"hardware" | "file">("hardware")
  const { status, adapter } = useEuSign()
  const fileLib = useEuSignFile(fileModeAvailable && mode === "file")
  const [keys, setKeys] = useState<KeyMediaDevice[]>([])
  const [keysError, setKeysError] = useState(false)
  const [selectedId, setSelectedId] = useState("")
  const [keyFile, setKeyFile] = useState<File | null>(null)
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isReading, setIsReading] = useState(false)
  const [readError, setReadError] = useState<string | null>(null)
  const [cas, setCas] = useState<CertificateAuthority[]>([])
  const [caValue, setCaValue] = useState(readStoredCa)

  useEffect(() => {
    if (status !== "ready" || !adapter) return
    let cancelled = false
    adapter
      .enumKeys()
      .then((result) => {
        if (cancelled) return
        setKeys(result)
        setSelectedId(
          (current) => current || (result[0] ? keyId(result[0]) : "")
        )
      })
      .catch(() => {
        if (!cancelled) setKeysError(true)
      })
    return () => {
      cancelled = true
    }
  }, [status, adapter])

  const isFileMode = fileModeAvailable && mode === "file"
  const activeAdapter = isFileMode ? fileLib.adapter : adapter

  // Both library types are initialized from the same CAs.json, so the list
  // only needs the adapter currently in use to be ready.
  useEffect(() => {
    if (!activeAdapter) return
    let cancelled = false
    activeAdapter
      .getCAs()
      .then((result) => {
        if (!cancelled) setCas(result)
      })
      .catch(() => {
        if (!cancelled) setCas([])
      })
    return () => {
      cancelled = true
    }
  }, [activeAdapter])

  // A remembered provider that is no longer in CAs.json falls back to auto.
  const caKnown = cas.some((ca) => ca.issuerCN === caValue)
  const effectiveCa = caValue !== AUTO_CA && caKnown ? caValue : AUTO_CA
  const caCN = effectiveCa === AUTO_CA ? undefined : effectiveCa

  const selected = keys.find((key) => keyId(key) === selectedId)
  const notReady = isFileMode ? fileLib.status !== "ready" : status !== "ready"
  const noKeys = !isFileMode && status === "ready" && keys.length === 0
  const readDisabled = isFileMode
    ? notReady || !keyFile || isReading || busy
    : notReady || noKeys || isReading || busy

  const handleRead = async () => {
    setIsReading(true)
    setReadError(null)
    try {
      if (isFileMode) {
        if (!keyFile || !fileLib.adapter) return
        const bytes = await readFileBytes(keyFile)
        const ownerInfo = await fileLib.adapter.readPrivateKeyFile(
          bytes,
          password,
          caCN
        )
        console.log(ownerInfo)
        onRead({ adapter: fileLib.adapter, keyLabel: keyFile.name })
      } else {
        if (!selected || !adapter) return
        const ownerInfo = await adapter.readPrivateKey(
          selected.typeIndex,
          selected.devIndex,
          password,
          caCN
        )
        console.log(ownerInfo)
        onRead({
          adapter,
          keyLabel: `${selected.typeName} · ${selected.devName}`,
        })
      }
    } catch {
      setReadError(
        isFileMode
          ? "Не вдалося зчитати файл ключа. Перевірте пароль і повторіть спробу."
          : "Не вдалося зчитати носій. Перевірте пароль і повторіть спробу."
      )
    } finally {
      setIsReading(false)
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (!readDisabled) void handleRead()
      }}
    >
      <fieldset className="space-y-4" disabled={disabled || isReading || busy}>
        {!isFileMode && status === "error" && (
          <ScenarioNotice tone="danger">
            Не вдалося ініціалізувати бібліотеку КЕП. Перевірте, що на
            комп'ютері встановлено агент підпису, і оновіть сторінку.
          </ScenarioNotice>
        )}
        {isFileMode && fileLib.status === "error" && (
          <ScenarioNotice tone="danger">
            Не вдалося ініціалізувати бібліотеку КЕП для файлового ключа.
          </ScenarioNotice>
        )}
        {readError && (
          <ScenarioNotice tone="danger">{readError}</ScenarioNotice>
        )}

        {fileModeAvailable && (
          <Field>
            <FieldLabel htmlFor="kep-mode">Тип носія (dev)</FieldLabel>
            <Select
              onValueChange={(value) => setMode(value as "hardware" | "file")}
              value={mode}
            >
              <SelectTrigger className="w-full" id="kep-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hardware">Апаратний носій</SelectItem>
                <SelectItem value="file">Файл ключа (dev)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        )}

        {isFileMode ? (
          <Field>
            <FieldLabel htmlFor="kep-key-file">Файл ключа</FieldLabel>
            <Input
              id="kep-key-file"
              onChange={(event) =>
                setKeyFile(event.target.files?.[0] ?? null)
              }
              type="file"
            />
            <FieldDescription>
              {fileLib.status === "loading"
                ? "Завантаження бібліотеки КЕП…"
                : keyFile
                  ? keyFile.name
                  : "Оберіть файл особистого ключа (наприклад, Key-6.dat)."}
            </FieldDescription>
          </Field>
        ) : (
          <Field>
            <FieldLabel htmlFor="kep-key">Носій особистого ключа</FieldLabel>
            <Select
              disabled={disabled || notReady || noKeys}
              onValueChange={setSelectedId}
              value={selectedId}
            >
              <SelectTrigger className="w-full" id="kep-key">
                <SelectValue placeholder="Носій не виявлено" />
              </SelectTrigger>
              <SelectContent>
                {keys.map((key) => (
                  <SelectItem key={keyId(key)} value={keyId(key)}>
                    {key.typeName} · {key.devName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldDescription>
              {status === "loading"
                ? "Завантаження бібліотеки КЕП…"
                : keysError
                  ? "Не вдалося отримати перелік носіїв від бібліотеки КЕП."
                  : selected
                    ? `${selected.typeName} · ${selected.devName}`
                    : "Вставте захищений носій у комп'ютер — перелік оновиться сам."}
            </FieldDescription>
          </Field>
        )}

        <Field>
          <FieldLabel htmlFor="kep-ca">
            Надавач електронних довірчих послуг
          </FieldLabel>
          <Select
            disabled={disabled || notReady || cas.length === 0}
            onValueChange={(value) => {
              setCaValue(value)
              storeCa(value)
            }}
            value={effectiveCa}
          >
            <SelectTrigger className="w-full" id="kep-ca">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO_CA}>Визначити автоматично</SelectItem>
              {cas.map((ca) => (
                <SelectItem key={ca.issuerCN} value={ca.issuerCN}>
                  {ca.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldDescription>
            {effectiveCa === AUTO_CA
              ? "Бібліотека опитає всіх надавачів по черзі — це довше."
              : "Сертифікат буде запитано лише в цього надавача."}
          </FieldDescription>
        </Field>

        <Field>
          <FieldLabel htmlFor="kep-password">Пароль</FieldLabel>
          <div className="relative">
            <Input
              className="pr-9"
              disabled={notReady}
              id="kep-password"
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••"
              type={showPassword ? "text" : "password"}
              value={password}
            />
            <button
              aria-label={showPassword ? "Приховати пароль" : "Показати пароль"}
              className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
              disabled={notReady}
              onClick={() => setShowPassword((current) => !current)}
              tabIndex={-1}
              type="button"
            >
              {showPassword ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
        </Field>

        <Button
          className="w-full"
          disabled={readDisabled}
          size={actionSize}
          type="submit"
        >
          {(isReading || busy) && (
            <Loader2 className="animate-spin" data-icon="inline-start" />
          )}
          {isReading ? "Зчитування…" : actionLabel}
        </Button>
      </fieldset>
    </form>
  )
}
